import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { shipmentsService } from '../services/logistics/shipments';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { clientsService } from '../services/logistics/clients';
import { truckTypesService } from '../services/logistics/truck-types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { citiesGrouped, saudiCities } from '../data/saudiCities';
import toast from 'react-hot-toast';
import ShipmentReportsModal from '../components/reports/ShipmentReportsModal';

const Shipments = () => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const { isRTL } = useTheme();
  const navigate = useNavigate();

  // Helper function to get city name in current language
  const getCityName = (cityKey) => {
    if (!cityKey) return '';
    
    // Search in all cities (both major and others)
    const city = saudiCities.find(c => c.value === cityKey);
    
    if (city) {
      return city.label[i18n.language] || city.label.en;
    }
    
    return cityKey; // Return as is if not found
  };

  // Helper function to format route display for new route system
  const formatRouteDisplay = (shipment) => {
    // If shipment has routeCities (new system), use that
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      const validCities = shipment.routeCities.filter(city => city.city);
      
      if (validCities.length === 0) {
        return { origin: 'N/A', destination: 'N/A' };
      }
      
      if (validCities.length === 1) {
        const cityName = getCityName(validCities[0].city);
        return { origin: cityName, destination: cityName };
      }
      
      // Get origin (first city)
      const originCity = getCityName(validCities[0].city);
      
      // Get destination cities (excluding origin)
      const destinationCities = validCities.slice(1).map(city => getCityName(city.city));
      
      if (destinationCities.length === 0) {
        return { origin: originCity, destination: originCity };
      }
      
      if (destinationCities.length === 1) {
        return { origin: originCity, destination: destinationCities[0] };
      }
      
      // Multiple destination cities - format as "City1 - City2 - City3"
      // If there are 3 or more destination cities, exclude the first destination city
      let citiesToShow = destinationCities;
      if (destinationCities.length >= 3) {
        citiesToShow = destinationCities.slice(1); // Skip first destination city
      }
      
      const destinationDisplay = citiesToShow.join(' - ');
      
      return { origin: originCity, destination: destinationDisplay };
    }
    
    // Fallback to old system
    return {
      origin: getCityName(shipment.originCity) || shipment.origin || 'N/A',
      destination: getCityName(shipment.destinationCity) || shipment.destination || 'N/A'
    };
  };

  // Helper function to get shipment type display
  const getShipmentTypeDisplay = (shipment) => {
    // For legacy shipments without shipmentType field
    if (!shipment.shipmentType) {
      const isLocal = shipment.originCity === shipment.destinationCity;
      return isLocal ? t('shipments.local_shipment') : t('shipments.outbound_shipment');
    }
    
    return shipment.shipmentType === 'local' ? t('shipments.local_shipment') : t('shipments.outbound_shipment');
  };

  // Helper function to get shipment type badge
  const getShipmentTypeBadge = (shipment) => {
    const isLocal = shipment.shipmentType === 'local' || (!shipment.shipmentType && shipment.originCity === shipment.destinationCity);
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isLocal 
          ? 'bg-green-100 text-green-800' 
          : 'bg-orange-100 text-orange-800'
      }`}>
        {getShipmentTypeDisplay(shipment)}
      </span>
    );
  };

  // Time period state — persisted in localStorage as user preference
  const [timePeriod, setTimePeriod] = useState(
    () => localStorage.getItem('shipmentTimePeriodPreference') || 'all'
  );
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [cityFromFilter, setCityFromFilter] = useState('all');
  const [cityToFilter, setCityToFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [shipments, setShipments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);

  // Bulk selection state
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!openMenuId) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openMenuId]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [shipmentsPerPage, setShipmentsPerPage] = useState(() => {
    return parseInt(localStorage.getItem('shipmentsPerPage')) || 10;
  });

  // Per page options
  const perPageOptions = [5, 10, 20, 50, 100];

  // Time period options that update with language changes
  const timePeriodOptions = useMemo(() => [
    { value: 'all', label: t('common.all_time', 'All Time') },
    { value: 'today', label: t('common.today', t('common.today')) },
    { value: 'yesterday', label: t('common.yesterday', t('common.yesterday')) },
    { value: 'this_week', label: t('common.this_week', t('common.this_week')) },
    { value: 'last_7_days', label: t('common.last_7_days', 'Last 7 Days') },
    { value: 'this_month', label: t('common.this_month', t('common.this_month')) },
    { value: 'last_30_days', label: t('common.last_30_days', 'Last 30 Days') },
    { value: 'this_quarter', label: t('common.this_quarter', t('common.this_quarter')) },
    { value: 'this_year', label: t('common.this_year', t('common.this_year')) },
    { value: 'last_year', label: t('common.last_year', t('common.last_year')) },
    { value: 'custom', label: t('common.custom_range', t('common.custom_range')) },
  ], [t]);

  // Get date range for selected time period
  const getDateRangeForPeriod = (period, customFrom = null, customTo = null) => {
    const today = new Date();
    
    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    switch (period) {
      case 'today':
        return {
          from: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
          to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
          label: formatDate(today)
        };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          from: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()),
          to: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999),
          label: formatDate(yesterday)
        };
      
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          from: startOfWeek,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(startOfWeek)} - ${formatDate(today)}`
        };
      
      case 'last_7_days':
        const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          from: sevenDaysAgo,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(sevenDaysAgo)} - ${formatDate(today)}`
        };
      
      case 'this_month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          from: startOfMonth,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(startOfMonth)} - ${formatDate(today)}`
        };
      
      case 'last_30_days':
        const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          from: thirtyDaysAgo,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(thirtyDaysAgo)} - ${formatDate(today)}`
        };
      
      case 'this_quarter':
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        const startOfQuarter = new Date(today.getFullYear(), quarterStartMonth, 1);
        return {
          from: startOfQuarter,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(startOfQuarter)} - ${formatDate(today)}`
        };
      
      case 'this_year':
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return {
          from: startOfYear,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: `${formatDate(startOfYear)} - ${formatDate(today)}`
        };
      
      case 'last_year':
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        return {
          from: lastYearStart,
          to: lastYearEnd,
          label: `${formatDate(lastYearStart)} - ${formatDate(lastYearEnd)}`
        };
      
      case 'custom':
        if (customFrom && customTo) {
          const fromDate = new Date(customFrom);
          const toDate = new Date(customTo + 'T23:59:59');
          return {
            from: fromDate,
            to: toDate,
            label: `${formatDate(fromDate)} - ${formatDate(toDate)}`
          };
        }
        return null;
      
      default:
        return null;
    }
  };

  const currentDateRange = getDateRangeForPeriod(timePeriod, customDateFrom, customDateTo);

  // Filter shipments by time period for metrics
  const getFilteredShipmentsForMetrics = () => {
    if (!shipments) return [];
    
    const startDate = currentDateRange?.from;
    const endDate = currentDateRange?.to;
    
    return shipments.filter(shipment => {
      const shipmentDate = shipment.createdAt?.toDate ? shipment.createdAt.toDate() : new Date(shipment.createdAt);
      return (!startDate || shipmentDate >= startDate) && (!endDate || shipmentDate <= endDate);
    });
  };

  const handleTimePeriodChange = (newPeriod) => {
    setTimePeriod(newPeriod);
    localStorage.setItem('shipmentTimePeriodPreference', newPeriod);
    setShowCustomDateRange(newPeriod === 'custom');
    if (newPeriod !== 'custom') {
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  useEffect(() => {
    if (!currentCompany?.id) {
      setLoading(false);
      return;
    }

    const companyId = currentCompany.id;

    const fetchAllData = async () => {
      try {
        const [shipmentsData, driversData, vehiclesData, truckTypesData, clientsData] = await Promise.all([
          shipmentsService.getShipments(companyId),
          driversService.getDrivers(companyId),
          vehiclesService.getVehicles(companyId),
          truckTypesService.getTruckTypes(companyId),
          clientsService.getClients(companyId),
        ]);
        setShipments(shipmentsData);
        setDrivers(driversData);
        setVehicles(vehiclesData);
        setTruckTypes(truckTypesData);
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching shipments page data:', error);
        toast.error('Failed to load shipments');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [currentCompany?.id]);

  // Status options that update with language changes  
  const statusOptions = useMemo(() => [
    { value: 'all', label: t('shipments.filter_all') },
    { value: 'pending', label: t('shipments.filter_pending') },
    { value: 'in_transit', label: t('shipments.filter_in_transit') },
    { value: 'delivered', label: t('shipments.filter_delivered') },
    { value: 'canceled', label: t('shipments.filter_canceled') },
  ], [t]);

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-warning', icon: ClockIcon },
      in_transit: { class: 'badge-info', icon: TruckIcon },
      delivered: { class: 'badge-success', icon: CheckCircleIcon },
      canceled: { class: 'badge-danger', icon: XCircleIcon },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`badge ${badge.class} inline-flex items-center`}>
        <Icon className="w-3 h-3 mr-1" />
        {t(`shipments.${status}`)}
      </span>
    );
  };

  const handleDeleteShipment = async (shipmentId) => {
    if (window.confirm(t('clients.confirm_delete'))) {
      try {
        await shipmentsService.deleteShipment(shipmentId);
        setShipments(prev => prev.filter(s => s.id !== shipmentId));
        toast.success('Shipment deleted successfully');
      } catch (error) {
        console.error('Error deleting shipment:', error);
        toast.error('Failed to delete shipment');
      }
    }
  };

  // Bulk selection handlers
  const handleSelectShipment = (shipmentId, checked) => {
    if (checked) {
      setSelectedShipments(prev => [...prev, shipmentId]);
    } else {
      setSelectedShipments(prev => prev.filter(id => id !== shipmentId));
      setIsSelectAll(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const eligibleShipments = currentShipments
        .filter(shipment => shipment.status !== 'delivered' && shipment.status !== 'canceled')
        .map(shipment => shipment.id);
      setSelectedShipments(eligibleShipments);
      setIsSelectAll(true);
    } else {
      setSelectedShipments([]);
      setIsSelectAll(false);
    }
  };

  const handleBulkMarkDelivered = async () => {
    if (selectedShipments.length === 0) {
      toast.error(t('shipments.no_shipments_selected', 'No shipments selected'));
      return;
    }

    const confirmMessage = t('shipments.confirm_bulk_delivered', 
      `Are you sure you want to mark ${selectedShipments.length} shipment(s) as delivered?`);
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const shipmentId of selectedShipments) {
        try {
          await shipmentsService.updateShipment(shipmentId, { 
            status: 'delivered',
            deliveredAt: new Date(),
            updatedBy: 'bulk_action'
          });
          successCount++;
        } catch (error) {
          console.error(`Error updating shipment ${shipmentId}:`, error);
          errorCount++;
        }
      }

      // Update local state
      setShipments(prev => prev.map(shipment => 
        selectedShipments.includes(shipment.id) 
          ? { ...shipment, status: 'delivered', deliveredAt: new Date() }
          : shipment
      ));

      // Clear selection
      setSelectedShipments([]);
      setIsSelectAll(false);

      // Show results
      if (successCount > 0) {
        toast.success(t('shipments.bulk_delivered_success', 
          `Successfully marked ${successCount} shipment(s) as delivered`));
      }
      if (errorCount > 0) {
        toast.error(t('shipments.bulk_delivered_errors', 
          `Failed to update ${errorCount} shipment(s)`));
      }

    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error(t('shipments.bulk_update_failed', 'Bulk update failed'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedShipments([]);
    setIsSelectAll(false);
  }, [searchTerm, statusFilter, dateFromFilter, dateToFilter, driverFilter, clientFilter, cityFromFilter, cityToFilter, vehicleFilter]);

  // Helper functions to get driver info
  const getDriverInfo = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver || null;
  };

  const getVehicleInfo = (vehicleIdOrPlate) => {
    // Try to find by ID first (both string and number)
    let vehicle = vehicles.find(v => v.id === vehicleIdOrPlate || v.id === parseInt(vehicleIdOrPlate));
    
    // If not found, try to find by plate number
    if (!vehicle) {
      vehicle = vehicles.find(v => v.plateNumber === vehicleIdOrPlate);
    }
    
    return vehicle || null;
  };

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };

  // Helper function to calculate total shipment price including all charges
  const calculateTotalShipmentPrice = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    // Calculate VAT
    let mainBasePrice, mainVatAmount;
    let backhaulVatAmount = backhaulPrice * 0.15;
    let itemizedVatAmount = itemizedTotal * 0.15;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, backhaul and itemized costs are without VAT
      mainBasePrice = baseShipmentPrice / 1.15;
      mainVatAmount = baseShipmentPrice - mainBasePrice;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
      mainVatAmount = baseShipmentPrice * 0.15;
    }

    const totalBasePrice = mainBasePrice + backhaulPrice + itemizedTotal;
    const totalVatAmount = mainVatAmount + backhaulVatAmount + itemizedVatAmount;
    const grandTotal = totalBasePrice + totalVatAmount;
    
    return grandTotal;
  };

  // Helper function to calculate subtotal before tax
  const calculateSubtotalBeforeTax = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    let mainBasePrice;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, extract base price
      mainBasePrice = baseShipmentPrice / 1.15;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
    }

    // Subtotal = base price + backhaul + itemized costs (all without VAT)
    const subtotal = mainBasePrice + backhaulPrice + itemizedTotal;
    
    return subtotal;
  };

  const filteredShipments = shipments.filter(shipment => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      shipment.trackingNumber.toLowerCase().includes(searchLower) ||
      shipment.clientName?.toLowerCase().includes(searchLower) ||
      shipment.origin?.toLowerCase().includes(searchLower) ||
      shipment.destination?.toLowerCase().includes(searchLower) ||
      getCityName(shipment.originCity)?.toLowerCase().includes(searchLower) ||
      getCityName(shipment.destinationCity)?.toLowerCase().includes(searchLower) ||
      shipment.driverName?.toLowerCase().includes(searchLower);

    // Status filter
    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
    
    // Date range filter
    let matchesDateRange = true;
    if (dateFromFilter || dateToFilter) {
      const shipmentDate = shipment.createdAt?.toDate ? shipment.createdAt.toDate() : new Date(shipment.createdAt);
      if (dateFromFilter) {
        matchesDateRange = matchesDateRange && shipmentDate >= new Date(dateFromFilter);
      }
      if (dateToFilter) {
        matchesDateRange = matchesDateRange && shipmentDate <= new Date(dateToFilter + 'T23:59:59');
      }
    }
    
    // Driver filter
    const matchesDriver = driverFilter === 'all' || shipment.driverId === driverFilter;
    
    // Client filter  
    const matchesClient = clientFilter === 'all' || shipment.clientId === clientFilter;
    
    // City filters (separate origin and destination)
    const matchesCityFrom = cityFromFilter === 'all' || shipment.originCity === cityFromFilter;
    const matchesCityTo = cityToFilter === 'all' || shipment.destinationCity === cityToFilter;
    
    // Vehicle filter
    const matchesVehicle = vehicleFilter === 'all' || shipment.vehiclePlate === vehicleFilter;
    
    return matchesSearch && matchesStatus && matchesDateRange && matchesDriver && matchesClient && matchesCityFrom && matchesCityTo && matchesVehicle;
  });

  const statistics = useMemo(() => {
    const filtered = getFilteredShipmentsForMetrics();
    return [
      {
        title: t('dashboard.total_shipments'),
        value: filtered.length.toLocaleString('en-US'),
        icon: TruckIcon,
        color: 'blue',
      },
      {
        title: t('dashboard.in_transit_now'),
        value: filtered.filter(s => s.status === 'in_transit').length.toLocaleString('en-US'),
        icon: TruckIcon,
        color: 'orange',
      },
      {
        title: t('dashboard.pending_pickup'),
        value: filtered.filter(s => s.status === 'pending').length.toLocaleString('en-US'),
        icon: ClockIcon,
        color: 'red',
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipments, timePeriod, customDateFrom, customDateTo, t]);

  // Helper function to format dates with English numerals
  const formatDateEnglish = (date) => {
    if (!date) return t('common.not_set');
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to format time with English numerals and proper AM/PM
  const formatTimeEnglish = (date) => {
    if (!date) return '';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    // Always use English format for time display regardless of language
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Pagination functions
  const handlePerPageChange = (newPerPage) => {
    setShipmentsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
    localStorage.setItem('shipmentsPerPage', newPerPage.toString());
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate pagination
  const totalShipments = filteredShipments.length;
  const totalPages = Math.ceil(totalShipments / shipmentsPerPage);
  const startIndex = (currentPage - 1) * shipmentsPerPage;
  const endIndex = startIndex + shipmentsPerPage;
  const currentShipments = filteredShipments.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFromFilter, dateToFilter, driverFilter, clientFilter, cityFromFilter, cityToFilter, vehicleFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div key={i18n.language} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('shipments.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('shipments.subtitle', 'Manage and track all your shipments')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3 rtl:space-x-reverse">
          <button
            onClick={() => setShowReportsModal(true)}
            className="btn bg-green-600 hover:bg-green-700 text-white inline-flex items-center border-green-600 hover:border-green-700"
          >
            <DocumentArrowDownIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {t('reports.excel_reports', 'Excel Reports')}
          </button>
          <Link
            to="/shipments/create"
            className="btn btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {t('shipments.create_shipment')}
          </Link>
        </div>
      </div>

      {/* Combined Metrics and Time Period Selector */}
      <div className="card">
        {/* Time Period Selector - Centered */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center space-x-4 rtl:space-x-reverse bg-gray-50 p-4 rounded-lg">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <label className="text-sm font-medium text-gray-700">
                {t('common.time_period', 'Time Period')}:
              </label>
              <select
                className="input-field min-w-[180px]"
                value={timePeriod}
                onChange={(e) => handleTimePeriodChange(e.target.value)}
              >
                {timePeriodOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Show current date range */}
            {currentDateRange && (
              <div className="text-sm text-gray-600 bg-white px-3 py-2 rounded-md border">
                <span className="font-medium">{t('common.showing_data_for', 'Showing data for')}:</span>
                <span className="ml-2">{currentDateRange.label}</span>
              </div>
            )}
          </div>

          {/* Custom Date Range Inputs */}
          {showCustomDateRange && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.from_date', 'From Date')}
                  </label>
                  <input
                    type="date"
                    className="input-field"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.to_date', 'To Date')}
                  </label>
                  <input
                    type="date"
                    className="input-field"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statistics.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'bg-blue-50 text-blue-600',
              green: 'bg-green-50 text-green-600',
              orange: 'bg-orange-50 text-orange-600',
              red: 'bg-red-50 text-red-600',
            };

            return (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="space-y-4">
          {/* Primary Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('shipments.search_placeholder')}
                  className="input-field pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                className="input-field min-w-[120px]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`btn ${showAdvancedFilters ? 'btn-primary' : 'btn-secondary'} text-sm`}
              >
                {t('common.advanced_filters', 'Advanced Filters')}
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="border-t pt-4 space-y-4">
              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.date_from', 'Date From')}
                  </label>
                  <input
                    type="date"
                    className="input-field text-sm"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.date_to', 'Date To')}
                  </label>
                  <input
                    type="date"
                    className="input-field text-sm"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shipments.filter_by_client', 'Filter by Client')}
                  </label>
                  <select
                    className="input-field text-sm"
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                  >
                    <option value="all">{t('common.all_clients', 'All Clients')}</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.companyName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shipments.filter_by_driver', 'Filter by Driver')}
                  </label>
                  <select
                    className="input-field text-sm"
                    value={driverFilter}
                    onChange={(e) => setDriverFilter(e.target.value)}
                  >
                    <option value="all">{t('common.all_drivers', 'All Drivers')}</option>
                    {drivers.map(driver => (
                      <option key={driver.id} value={driver.id}>{driver.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shipments.filter_by_vehicle', 'Filter by Vehicle')}
                  </label>
                  <select
                    className="input-field text-sm"
                    value={vehicleFilter}
                    onChange={(e) => setVehicleFilter(e.target.value)}
                  >
                    <option value="all">{t('common.all_vehicles', 'All Vehicles')}</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.plateNumber}>
                        {vehicle.plateNumber} - {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* City Filter */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shipments.filter_by_city_from', 'Filter by City From')}
                  </label>
                  <select
                    className="input-field text-sm"
                    value={cityFromFilter}
                    onChange={(e) => setCityFromFilter(e.target.value)}
                  >
                    <option value="all">{t('common.all_cities', 'All Cities')}</option>
                    <optgroup label={t('shipments.major_cities')}>
                      {citiesGrouped.major.map(city => (
                        <option key={city.value} value={city.value}>
                          {i18n.language === 'ar' ? city.label.ar : city.label.en}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label={t('shipments.other_cities')}>
                      {citiesGrouped.others.map(city => (
                        <option key={city.value} value={city.value}>
                          {i18n.language === 'ar' ? city.label.ar : city.label.en}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shipments.filter_by_city_to', 'Filter by City To')}
                  </label>
                  <select
                    className="input-field text-sm"
                    value={cityToFilter}
                    onChange={(e) => setCityToFilter(e.target.value)}
                  >
                    <option value="all">{t('common.all_cities', 'All Cities')}</option>
                    <optgroup label={t('shipments.major_cities')}>
                      {citiesGrouped.major.map(city => (
                        <option key={city.value} value={city.value}>
                          {i18n.language === 'ar' ? city.label.ar : city.label.en}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label={t('shipments.other_cities')}>
                      {citiesGrouped.others.map(city => (
                        <option key={city.value} value={city.value}>
                          {i18n.language === 'ar' ? city.label.ar : city.label.en}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setDateFromFilter('');
                      setDateToFilter('');
                      setClientFilter('all');
                      setDriverFilter('all');
                      setCityFromFilter('all');
                      setCityToFilter('all');
                      setVehicleFilter('all');
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    {t('common.clear_filters', 'Clear Filters')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedShipments.length > 0 && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <span className="text-sm font-medium text-blue-900">
                {t('shipments.selected_count', `${selectedShipments.length} shipment(s) selected`)}
              </span>
              <button
                onClick={() => {
                  setSelectedShipments([]);
                  setIsSelectAll(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('common.clear_selection', 'Clear Selection')}
              </button>
            </div>
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <button
                onClick={handleBulkMarkDelivered}
                disabled={bulkActionLoading}
                className="btn bg-green-600 hover:bg-green-700 text-white inline-flex items-center border-green-600 hover:border-green-700 disabled:opacity-50"
              >
                {bulkActionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                    {t('shipments.mark_delivered', 'Mark as Delivered')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipments Table */}
      <div className="card">
        {filteredShipments.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <table dir={isRTL ? 'rtl' : 'ltr'} className="table w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="table-header w-8 px-1 sm:px-2">
                    <input
                      type="checkbox"
                      checked={isSelectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title={t('common.select_all', 'Select All')}
                    />
                  </th>
                  <th className="table-header">{t('shipments.shipment_number')}</th>
                  <th className="table-header">{t('shipments.client')}</th>
                  <th className="table-header">{t('shipments.origin')}</th>
                  <th className="table-header">{t('shipments.destination')}</th>
                  <th className="table-header">{t('shipments.driver')}</th>
                  <th className="table-header">{t('shipments.vehicle_plate')}</th>
                  <th className="table-header">{t('shipments.status')}</th>
                  <th className="table-header">{t('shipments.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentShipments.map((shipment) => {
                  const isEligibleForSelection = shipment.status !== 'delivered' && shipment.status !== 'canceled';
                  const isSelected = selectedShipments.includes(shipment.id);
                  
                  return (
                    <tr key={shipment.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/shipments/${shipment.id}`)}>
                      <td className="table-cell w-8 px-1 sm:px-2" onClick={(e) => e.stopPropagation()}>
                        {isEligibleForSelection ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectShipment(shipment.id, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        ) : (
                          <div className="w-4 h-4"></div>
                        )}
                      </td>
                      <td className="table-cell whitespace-nowrap">
                        <div className="font-medium text-gray-900 text-sm">{shipment.trackingNumber}</div>
                        <div className="text-[11px] text-gray-500 leading-tight">
                          {formatDateEnglish(shipment.createdAt)} {formatTimeEnglish(shipment.createdAt)}
                        </div>
                      </td>
                      <td className="table-cell max-w-[160px]">
                        <div className="text-sm text-gray-900 truncate" title={shipment.clientName || ''}>
                          {shipment.clientName || t('common.not_available', 'N/A')}
                        </div>
                      </td>
                      <td className="table-cell max-w-[120px]">
                        {(() => {
                          const route = formatRouteDisplay(shipment);
                          const site = shipment.routeCities?.[0]?.site;
                          const full = site ? `${route.origin} — ${site}` : route.origin;
                          return (
                            <div className="text-sm text-gray-900 truncate" title={full}>
                              {route.origin}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="table-cell max-w-[120px]">
                        {(() => {
                          const route = formatRouteDisplay(shipment);
                          const last = shipment.routeCities?.[shipment.routeCities.length - 1];
                          const site = last?.site;
                          const full = site ? `${route.destination} — ${site}` : route.destination;
                          return (
                            <div className="text-sm text-gray-900 truncate" title={full}>
                              {route.destination}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="table-cell max-w-[160px]">
                        {(() => {
                          const driver = getDriverInfo(shipment.driverId);
                          const name = (driver?.nickname && driver.nickname.trim())
                            ? driver.nickname
                            : (driver?.name || shipment.driverName || t('common.not_available', 'N/A'));
                          return (
                            <div className="flex items-center gap-1.5 min-w-0" title={name}>
                              <span className="text-sm text-gray-900 truncate">{name}</span>
                              {shipment.isExternalDriver && (
                                <span className="badge bg-purple-100 text-purple-700 !px-1.5 text-[10px] flex-shrink-0">
                                  {t('common.external', 'External')}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="table-cell whitespace-nowrap">
                        {(() => {
                          let plate;
                          if (shipment.isExternalDriver) {
                            plate = t('common.external', 'External');
                          } else if (shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                            plate = shipment.vehiclePlate;
                          } else if (shipment.vehicleId) {
                            const vehicle = getVehicleInfo(shipment.vehicleId);
                            plate = vehicle?.plateNumber || t('common.not_assigned', 'Not Assigned');
                          } else {
                            plate = t('common.not_assigned', 'Not Assigned');
                          }

                          let truckType = '';
                          if (shipment.isExternalDriver) {
                            truckType = getTruckTypeName(shipment.truckType) || '';
                          } else {
                            let vehicle = null;
                            if (shipment.vehicleId) vehicle = getVehicleInfo(shipment.vehicleId);
                            if (!vehicle && shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                              vehicle = getVehicleInfo(shipment.vehiclePlate);
                            }
                            if (vehicle) truckType = getTruckTypeName(vehicle.truckType || vehicle.type) || '';
                          }

                          return (
                            <div className="text-sm text-gray-900" title={truckType}>
                              {plate}
                              {truckType && <span className="text-gray-500 text-xs ml-1 rtl:mr-1 rtl:ml-0">· {truckType}</span>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(shipment.status)}
                      </td>
                      <td className="table-cell whitespace-nowrap w-10" onClick={(e) => e.stopPropagation()}>
                        <div className="relative" ref={openMenuId === shipment.id ? menuRef : null}>
                          <button
                            type="button"
                            onClick={() => setOpenMenuId(openMenuId === shipment.id ? null : shipment.id)}
                            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            title={t('shipments.actions')}
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === shipment.id}
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </button>
                          {openMenuId === shipment.id && (
                            <div
                              role="menu"
                              className="absolute z-20 mt-1 end-0 w-36 rounded-md bg-white shadow-lg ring-1 ring-black/5 py-1 text-sm"
                            >
                              <Link
                                to={`/shipments/${shipment.id}`}
                                role="menuitem"
                                onClick={() => setOpenMenuId(null)}
                                className="flex items-center gap-2 px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                              >
                                <EyeIcon className="h-4 w-4 text-blue-600" />
                                {t('shipments.view')}
                              </Link>
                              <Link
                                to={`/shipments/${shipment.id}/edit`}
                                role="menuitem"
                                onClick={() => setOpenMenuId(null)}
                                className="flex items-center gap-2 px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                              >
                                <PencilIcon className="h-4 w-4 text-green-600" />
                                {t('shipments.edit')}
                              </Link>
                              <button
                                role="menuitem"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleDeleteShipment(shipment.id);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50 text-start"
                              >
                                <TrashIcon className="h-4 w-4" />
                                {t('shipments.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('shipments.no_shipments_found', 'No shipments found')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {shipments.length === 0 
                ? t('shipments.get_started_first_shipment', 'Get started by creating your first shipment.')
                : t('shipments.adjust_search_criteria', 'Try adjusting your search or filter criteria.')
              }
            </p>
            <div className="mt-6 flex items-center justify-center space-x-3 rtl:space-x-reverse">
              {shipments.length > 0 && (
                <button
                  onClick={() => setShowReportsModal(true)}
                  className="btn bg-green-600 hover:bg-green-700 text-white inline-flex items-center border-green-600 hover:border-green-700"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('reports.excel_reports', 'Excel Reports')}
                </button>
              )}
              <Link
                to="/shipments/create"
                className="btn btn-primary inline-flex items-center"
              >
                <PlusIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('shipments.create_shipment')}
              </Link>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {totalShipments > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            {/* Per Page Selector */}
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <span className="text-sm text-gray-700">{t('common.show')}:</span>
              <select
                value={shipmentsPerPage}
                onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                className="input-field text-sm py-1 px-2 min-w-[80px]"
              >
                {perPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">{t('common.per_page')}</span>
            </div>

            {/* Pagination Info */}
            <div className="flex items-center text-sm text-gray-700">
              <span>
                {t('common.showing')} {startIndex + 1} {t('common.to')} {Math.min(endIndex, totalShipments)} {t('common.of')} {totalShipments} {t('shipments.title').toLowerCase()}
              </span>
            </div>

            {/* Pagination Buttons */}
            {totalPages > 1 && (
              <div className="flex items-center space-x-1">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">{t('common.previous')}</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 rounded-md border text-sm font-medium ${
                        currentPage === pageNumber
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">{t('common.next')}</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Excel Reports Modal */}
      <ShipmentReportsModal
        isOpen={showReportsModal}
        onClose={() => setShowReportsModal(false)}
      />
    </div>
  );
};

export default Shipments;
